/**
 * Goods receipting at the Lite (internal Free) 1-PO cap.
 * Copy comes from Express (`goods_receipting_limit_reached`). Do not invent
 * a journey from featureId. Paid at-limit stays help-led (no upgrade tone).
 */

import {
  hasGrowthEngineUpgradeTone,
  isPaidGrowthEnginePlan,
} from './growthEngineHumanPath'
import {
  FREE_LIMIT_HELP_FIRST_TITLE,
  applyFreeLimitHelpFirstTitle,
} from './growthEngineFreeLimitHelpFirst'

export const GOODS_RECEIPTING_LIMIT_REACHED_INTENT =
  'goods_receipting_limit_reached'

/** Express stage4 — used only when GET has not loaded yet. */
export const GOODS_RECEIPTING_FREE_LIMIT_FALLBACK = Object.freeze({
  title: FREE_LIMIT_HELP_FIRST_TITLE,
  body: 'OMS Lite includes 1 checked PO a month, and you did it. Checking deliveries is how kitchens get credits they would otherwise miss; OMS keeps those credits and the numbers honest. We will walk through the next one with you. Starter receipts more POs when you are ready, or book a demo to see receipting plus orders, invoices, and GP.',
})

export const GOODS_RECEIPTING_PAID_LIMIT_FALLBACK = Object.freeze({
  title: 'This month’s receipting allowance is used',
  body: 'You’ve checked the deliveries your plan includes this month. Want us to walk through the next one with you, or look at how receipting fits the rest of the kitchen?',
})

export const GOODS_RECEIPTING_PLAN_PILLS = Object.freeze([
  'Starter — 5 POs/month',
  'Growth — 10 POs/month',
  'Pro — unlimited',
])

const asTrimmed = value => {
  if (value == null) return ''
  return String(value).trim()
}

const paidHelpLed = () => ({
  title: GOODS_RECEIPTING_PAID_LIMIT_FALLBACK.title,
  body: GOODS_RECEIPTING_PAID_LIMIT_FALLBACK.body,
  progress: null,
  sellStarter: false,
  showPlanPills: false,
})

/**
 * @param {object} [params]
 * @param {object|null} [params.recommendation]
 * @param {object|null} [params.guidedSuccess]
 * @param {string|null} [params.plan]
 */
export function getGoodsReceiptingLimitReachedCopy({
  recommendation,
  guidedSuccess,
  plan,
} = {}) {
  const paid = isPaidGrowthEnginePlan(plan)
  const freeHelpFirst = copy =>
    paid
      ? copy
      : {
          ...copy,
          title: applyFreeLimitHelpFirstTitle(copy.title, plan),
        }

  if (recommendation?.intentId === GOODS_RECEIPTING_LIMIT_REACHED_INTENT) {
    const title = asTrimmed(recommendation.title)
    const body = asTrimmed(recommendation.body)
    if (title || body) {
      if (paid && hasGrowthEngineUpgradeTone(`${title} ${body}`)) {
        return paidHelpLed()
      }
      return freeHelpFirst({
        title,
        body,
        progress: asTrimmed(recommendation.progress) || null,
        sellStarter: !paid,
        showPlanPills: !paid,
      })
    }
  }

  const next = guidedSuccess?.nextAction
  if (
    next?.intentId === GOODS_RECEIPTING_LIMIT_REACHED_INTENT &&
    asTrimmed(next.summary)
  ) {
    const summary = asTrimmed(next.summary)
    if (paid && hasGrowthEngineUpgradeTone(summary)) {
      return paidHelpLed()
    }
    return freeHelpFirst({
      title: paid
        ? GOODS_RECEIPTING_PAID_LIMIT_FALLBACK.title
        : GOODS_RECEIPTING_FREE_LIMIT_FALLBACK.title,
      body: summary,
      progress: null,
      sellStarter: !paid,
      showPlanPills: !paid,
    })
  }

  if (paid) {
    return paidHelpLed()
  }

  return freeHelpFirst({
    title: GOODS_RECEIPTING_FREE_LIMIT_FALLBACK.title,
    body: GOODS_RECEIPTING_FREE_LIMIT_FALLBACK.body,
    progress: null,
    sellStarter: true,
    showPlanPills: true,
  })
}

export function shouldShowGoodsReceiptingLimitReached({
  remaining,
  plan,
  loading = false,
} = {}) {
  if (loading) return false
  if (plan === 'Pro') return false
  if (remaining == null) return false
  return Number(remaining) <= 0
}
